import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { healthAPI } from '../api';
import { UserProfile } from '../types';

interface UserContextType {
    profile: UserProfile | null;
    loading: boolean;
    error: string | null;
    refreshProfile: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const refreshProfile = async () => {
        try {
            // Don't set loading to true on refresh to avoid full screen spinners if we just want to update data
            // But for initial load it is true.
            // If we want to show loading during refresh, we can manage detailed state, but for now let's keep it simple.
            // We'll only set loading true if we don't have a profile yet.
            if (!profile) setLoading(true);

            const data = await healthAPI.getUserProfile();
            setProfile(data);
            setError(null);
        } catch (err) {
            console.error("Failed to load user profile", err);
            setError('Failed to load user profile');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshProfile();
    }, []);

    return (
        <UserContext.Provider value={{ profile, loading, error, refreshProfile }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
